import { useTranslation } from "@sg/i18n";
import { invalidateCache } from "@sg/query";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { z } from "zod";
import useAuthStore from "~/store/auth";
import { AuditableSchema, RoleV2 } from "~/types";
import { agent } from "~/util/agent";

const UserBaseSchema = z.object({
  org_id: z.string().uuid().min(1),
  first_name: z.string().trim().min(1),
  last_name: z.string().trim().min(1),
  email: z.string().trim().email().min(1),
  can_change_password: z.boolean().optional(),
  is_active: z.boolean().optional(),
  telegram_id: z.string().optional(),
});

const ProfileUpdateSchema = z.object({
  telegram_id: z.string().optional(),
});

const UserCreateSchema = UserBaseSchema.extend({
  is_m2m: z.boolean(),
  role: z.nativeEnum(RoleV2),
});

const UserSchema = UserBaseSchema.extend({
  roles: z.array(
    z.object({
      role: z.nativeEnum(RoleV2),
    })
  ),
}).merge(AuditableSchema);

type ProfileUpdateType = z.infer<typeof ProfileUpdateSchema>;
type UserResourceType = z.infer<typeof UserSchema>;
type CreateUserInput = z.infer<typeof UserCreateSchema>;

const QUERY_USERS_KEY = "users";

const useMe = () =>
  useQuery([QUERY_USERS_KEY, "me"], async () => agent("users/me").json<UserResourceType>(), {
    enabled: false,
  });

const useUsers = (orgId?: string | null) =>
  useQuery([QUERY_USERS_KEY, "org", orgId], async () =>
    agent(`users/${orgId ? `?org_id=${orgId}` : ""}`).json<UserResourceType[]>()
  );

const useUser = (id?: string) =>
  useQuery([QUERY_USERS_KEY, id], async () => agent(`users/${id}`).json<UserResourceType>(), {
    enabled: !!id,
  });

const useCreateUser = () => {
  return useMutation<unknown, Error, CreateUserInput>(
    (data) => {
      return agent.post("users/", { labels: ["created", "user"], json: data });
    },
    {
      onSuccess(data, variables) {
        invalidateCache([QUERY_USERS_KEY], variables);
        invalidateCache([QUERY_USERS_KEY, "org", null], variables);
      },
    }
  );
};

const useUpdateUser = (id?: string) => {
  const { orgId } = useParams();

  return useMutation<unknown, Error, ProfileUpdateType>(
    (data) => {
      return agent.patch(`users/${id}`, { labels: ["updated", "user"], json: data });
    },
    {
      onSuccess(data, variables) {
        invalidateCache([QUERY_USERS_KEY, id], variables);
        invalidateCache([QUERY_USERS_KEY, "org", orgId || null], variables);
      },
    }
  );
};

const useUpdateProfile = () => {
  const userInfo = useAuthStore((state) => state.userInfo);
  const setTelegramId = useAuthStore((state) => state.setTelegramId);
  return useMutation<unknown, Error, ProfileUpdateType>(
    (data) => {
      return agent.patch(`users/${userInfo.id}`, { labels: ["updated", "profile"], json: data });
    },
    {
      onSuccess(data, variables) {
        setTelegramId(variables.telegram_id || "");
        invalidateCache([QUERY_USERS_KEY, userInfo.id], variables);
        invalidateCache([QUERY_USERS_KEY, "me"], variables);
      },
    }
  );
};

const useResetPassword = () => {
  const { t } = useTranslation();
  const userInfo = useAuthStore((state) => state.userInfo);
  return useMutation(() => {
    return agent.patch("users/change_passwords", {
      okLabels: [t("reset_password_success", { email: userInfo.email })],
      errLabels: [t("reset_password_failure")],
    });
  });
};

const useDeactivateUser = () => {
  return useMutation<unknown, Error, string>(
    (memberId) => {
      return agent.delete(`users/${memberId}`, { labels: ["deactivated", "user"] });
    },
    {
      onSuccess(data, variables) {
        invalidateCache([QUERY_USERS_KEY], variables);
      },
    }
  );
};

const useResendInvitation = () => {
  return useMutation<unknown, Error, string>((memberId) => {
    return agent.post(`users/${memberId}/resend_invite`, { labels: ["resent_invitation", "user"] });
  });
};

export {
  ProfileUpdateSchema,
  QUERY_USERS_KEY,
  useCreateUser,
  useDeactivateUser,
  useMe,
  UserCreateSchema,
  useResendInvitation,
  useResetPassword,
  UserSchema,
  useUpdateProfile,
  useUpdateUser,
  useUser,
  useUsers,
};
export type { CreateUserInput, UserResourceType };
